import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Printer } from 'lucide-react';
import {
  useGetClassResultsQuery,
  getApiError,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { ReportCardSheet } from './ReportCardSheet';

/*
  ═════════════════════════════════════════════════════════════════════════════
  REPORT CARDS, ON PAPER

  Why this exists at all
  ──────────────────────
  Ghanaian schools hand parents a report card at the end of term. Many of the
  families SafePass is built for read it on paper and nowhere else — the same
  families the SMS-first design exists for. A results screen that only lives
  behind a login serves the parents who need it least.

  Why it is a separate route and not a print stylesheet over the dashboard
  ───────────────────────────────────────────────────────────────────────
  Printing the dashboard means hiding a sidebar, a command bar, a breadcrumb
  and a table chrome, and then discovering on paper that one of them survived.
  This renders the document itself: black on white, one card per page, nothing
  on screen that is not on the page except the two buttons, which carry
  `no-print`.

  Why black on white regardless of theme
  ──────────────────────────────────────
  The app is dark. A dark report card prints as a black rectangle, or — with
  background graphics off, which is the default — as grey text with every
  border missing. Neither is something to hand a parent, so this page does not
  use the theme tokens at all. It is a document, and documents are ink on
  paper.

  Why ranks carry what they are out of
  ────────────────────────────────────
  "3rd" alone is unreadable: third of nine and third of forty-five are not the
  same news, and a parent holding paper cannot tap anything to find out which.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function ReportCardsPrintPage() {
  const { examId = '' } = useParams();
  const [params] = useSearchParams();
  const groupId = params.get('groupId') ?? '';
  const only = params.get('studentId') ?? '';
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');
  const navigate = useNavigate();

  const {
    data: cards = [],
    isLoading,
    isError,
    error,
  } = useGetClassResultsQuery({ institutionId, examId, groupId }, { skip: !institutionId || !examId || !groupId });


  /* A child with no marks has no report card — printing an empty one wastes a
     sheet and tells a parent nothing. */
  const printable = cards.filter((c) => c.subjectsTaken > 0 && (!only || c.studentId === only));

  /* The browser keeps its own white page; ours would otherwise be a dark strip
     on it. Restored on the way out so the dashboard is unaffected. */
  useEffect(() => {
    const previous = document.body.style.background;
    document.body.style.background = '#fff';
    return () => {
      document.body.style.background = previous;
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f1f3f6] py-6 text-black">
      <div className="no-print mx-auto mb-5 flex max-w-[820px] items-center gap-2 px-4">
        <Button variant="secondary" size="sm" onClick={() => navigate(`/exams/${examId}`)}>
          <ArrowLeft size={13} className="mr-1" />
          Back to marks
        </Button>
        <span className="text-[11.5px] text-neutral-600">
          {printable.length} report card{printable.length === 1 ? '' : 's'}
          {cards.length > printable.length && ` · ${cards.length - printable.length} child${cards.length - printable.length === 1 ? '' : 'ren'} with no marks left out`}
        </span>
        <Button variant="primary" size="sm" className="ml-auto" onClick={() => window.print()}>
          <Printer size={13} className="mr-1" />
          Print
        </Button>
      </div>

      {isError && (
        <p role="alert" className="mx-auto max-w-[820px] px-4 text-[12px] font-medium text-red-700">
          {getApiError(error)}
        </p>
      )}

      {!isError && printable.length === 0 && (
        <p className="mx-auto max-w-[820px] px-4 text-[12px] text-neutral-600">
          No marks have been entered for this class yet, so there is nothing to print.
        </p>
      )}

      {printable.map((card) => (
        <ReportCardSheet key={card.studentId} card={card} />
      ))}
    </div>
  );
}

